import React, { useMemo, useState, useEffect } from "react";
import { useRecoilState } from "recoil";
import { timezoneAtom } from "@/Atoms/settings/TimezoneAtom";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { getTimezones, formatDateTime } from "@/helpers/helperFunctions";
import { useTranslation } from "next-i18next";
import { Check, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import moment from "moment";
import moment_tz from "moment-timezone";

export default function TimezoneSelector() {
  const { t } = useTranslation("layout-page");
  const [timezone, setTimezone] = useRecoilState(timezoneAtom);
  const [open, setOpen] = useState(false);
  const [currentTime, setCurrentTime] = useState("");

  useEffect(() => {
    const updateTime = () => {
      // Use the existing formatDateTime helper to get the time in the selected tz
      const timeStr = formatDateTime(new Date(), "HH:mm:ss");

      // Extract the short timezone name or offset
      let shortTz = "UTC";
      if (timezone && timezone !== "UTC") {
        try {
          // If it's an IANA name, format the current offset
          shortTz = `UTC${moment_tz.tz(moment(), timezone).format("Z")}`;
        } catch {
          // Fallback to legacy offset parsing if it's still a "(UTC-8) Los Angeles" string
          const tzMatch = timezone.match(
            /\((GMT|UTC)([+-]\d{1,2}(?::\d{2})?)\)/,
          );
          if (tzMatch) {
            shortTz = `UTC${tzMatch[2]}`;
          }
        }
      }

      setCurrentTime(`${timeStr} (${shortTz})`);
    };

    updateTime(); // Initial call
    const interval = setInterval(updateTime, 1000); // Update every second

    return () => clearInterval(interval);
  }, [timezone]);

  const formattedTimezones = useMemo(() => {
    const list = getTimezones();
    const result = list.map((tz) => {
      const iana = tz.iana;
      const name = tz.name;

      if (iana === "UTC") return { value: "UTC", label: "UTC" };
      try {
        const now = moment();
        const offset = moment_tz.tz(now, iana).format("Z");
        return { value: iana, label: `(UTC${offset}) ${name}` };
      } catch {
        return { value: iana, label: name };
      }
    });

    // Ensure UTC is at the top, then sort others by offset then label
    const utcItem = result.find((item) => item.value === "UTC");
    const others = result
      .filter((item) => item.value !== "UTC")
      .sort((a, b) => a.label.localeCompare(b.label));

    return utcItem ? [utcItem, ...others] : others;
  }, []);

  return (
    <div className="flex items-center gap-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild className="flex">
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-[150px] h-8 text-xs justify-between shadow-none focus:ring-0 bg-transparent px-2 justify-end"
          >
            <span className="truncate">
              {currentTime || t("Select Timezone")}
            </span>
            <ChevronDown className=" h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[250px] p-0" align="end">
          <Command>
            <CommandInput
              placeholder={t("Search timezone...")}
              className="h-9"
            />
            <CommandList>
              <CommandEmpty>{t("No timezone found.")}</CommandEmpty>
              <CommandGroup>
                {formattedTimezones.map((tzItem) => (
                  <CommandItem
                    key={tzItem.value}
                    value={tzItem.label} // Command uses value for filtering, so label is better here
                    onSelect={() => {
                      setTimezone(tzItem.value);
                      setOpen(false);
                    }}
                    className="text-xs"
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        timezone === tzItem.value ? "opacity-100" : "opacity-0",
                      )}
                    />
                    {tzItem.label}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
